import { useLazyQuery } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { GetLalSegmentsBuildAudienceDocument } from '../../../../../../../../../graphql/query/audiences/GetLalSegmentsBuildAudience.generated';
import {
  type LookalikeFormSegment,
  type LookalikeSegmentDefinition,
  lookalikeSpecificCategory,
  type SimilarityLevelValue,
} from '../../../../../../types';
import { transformLalDefinitions } from '../../../../hooks/utils/transformLalDefinitions';
import { createSegmentsFromLookalikeDefinition } from '../../components/SegmentsList/utils/createSegmentsFromLookalikeDefinition';
import { isFormSegmentSelectionReturnType } from '../../utils';
import type { UseLookalikeModal } from '../types';

export const useLookalikeModal: UseLookalikeModal = ({ segmentSelectionProps, fetchedSegments }) => {
  const [showLookalikeModal, setShowLookalikeModal] = useState(false);
  const [selectedDefinitionForModal, setSelectedDefinitionForModal] = useState<LookalikeSegmentDefinition | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleLalDefinitionSelect = useCallback(
    (definition: LookalikeSegmentDefinition) => {
      setSelectedDefinitionForModal(definition);

      if (!isFormSegmentSelectionReturnType(segmentSelectionProps)) {
        const existingDefinition = segmentSelectionProps.segmentsByDefinition[definition.id];

        setIsEditMode(!!existingDefinition?.similaritySegment);
      }

      setShowLookalikeModal(true);
    },
    [segmentSelectionProps]
  );

  const [getLookalikeSegments] = useLazyQuery(GetLalSegmentsBuildAudienceDocument);

  const handleModalConfirm = useCallback(
    (selection: { similarityLevel: SimilarityLevelValue; includeSeedSegment: boolean }) => {
      if (!selectedDefinitionForModal || isFormSegmentSelectionReturnType(segmentSelectionProps)) return;

      const newSegments = createSegmentsFromLookalikeDefinition(selectedDefinitionForModal, selection);
      const definitionId = selectedDefinitionForModal.id;

      newSegments.forEach(segment => {
        if (segment.sub_category === lookalikeSpecificCategory.Similarity) {
          if (isEditMode) {
            segmentSelectionProps.editSimilaritySegment(definitionId, segment, selection.similarityLevel);
          } else {
            segmentSelectionProps.addSimilaritySegment(definitionId, segment, selection.similarityLevel);
          }
        } else if (segment.sub_category === lookalikeSpecificCategory.SeedAggregate) {
          segmentSelectionProps.addSeedSegment(definitionId, segment);
        }
      });

      if (isEditMode && !selection.includeSeedSegment) {
        const existingDefinition = segmentSelectionProps.segmentsByDefinition[definitionId];
        if (existingDefinition?.seedSegment) {
          segmentSelectionProps.removeSeedSegment(definitionId);
        }
      }

      setSelectedDefinitionForModal(null);
      setIsEditMode(false);
      setShowLookalikeModal(false);
    },
    [selectedDefinitionForModal, segmentSelectionProps, isEditMode]
  );

  const handleModalClose = useCallback(() => {
    setSelectedDefinitionForModal(null);
    setIsEditMode(false);
    setShowLookalikeModal(false);
  }, []);

  const handleEditLookalike = useCallback(
    async (segment: LookalikeFormSegment) => {
      if (!segment.lookalike_definition_id) {
        return;
      }

      const definition = fetchedSegments?.find(def => def.id === segment.lookalike_definition_id);

      if (definition) {
        handleLalDefinitionSelect(definition);
      }

      if (!definition) {
        const accountId = segment.account_id ?? '';
        const lookalikeDefinitionId = segment.lookalike_definition_id;

        try {
          const { data } = await getLookalikeSegments({
            variables: {
              where: {
                id: { _eq: lookalikeDefinitionId },
              },
              limit: 1,
            },
          });

          const definition = transformLalDefinitions(accountId, data?.lalSegmentFeed)[0];

          handleLalDefinitionSelect(definition);
        } catch (_) {
          enqueueSnackbar('Error fetching lookalike segments', { variant: 'error' });
        }
      }
    },
    [fetchedSegments, handleLalDefinitionSelect, enqueueSnackbar, getLookalikeSegments]
  );

  return {
    showLookalikeModal,
    selectedDefinitionForModal,
    isEditMode,
    handleLalDefinitionSelect,
    handleModalConfirm,
    handleModalClose,
    handleEditLookalike,
  };
};
